// One interface, four very different browser APIs behind it.
//
// The ELM327 layer talks only to this, so adding a transport never touches
// protocol code and swapping transports never touches the UI.

export type TransportKind = 'wifi' | 'serial' | 'ble' | 'sim';

export type TransportState = 'idle' | 'connecting' | 'open' | 'closing' | 'closed' | 'error';

export interface TransportStatus {
  state: TransportState;
  detail?: string;
}

export interface Transport {
  readonly kind: TransportKind;
  readonly label: string;
  readonly isOpen: boolean;
  open(): Promise<void>;
  close(): Promise<void>;
  write(bytes: Uint8Array): Promise<void>;
  onData(cb: (chunk: Uint8Array) => void): () => void;
  onStatus(cb: (status: TransportStatus) => void): () => void;
}

/** Minimal listener plumbing shared by every transport. */
export class TransportBase {
  protected dataListeners = new Set<(chunk: Uint8Array) => void>();
  protected statusListeners = new Set<(s: TransportStatus) => void>();
  protected status: TransportStatus = { state: 'idle' };

  onData(cb: (chunk: Uint8Array) => void) {
    this.dataListeners.add(cb);
    return () => this.dataListeners.delete(cb);
  }

  onStatus(cb: (s: TransportStatus) => void) {
    this.statusListeners.add(cb);
    cb(this.status);
    return () => this.statusListeners.delete(cb);
  }

  protected emitData(chunk: Uint8Array) {
    for (const cb of this.dataListeners) cb(chunk);
  }

  protected emitStatus(state: TransportState, detail?: string) {
    this.status = { state, detail };
    for (const cb of this.statusListeners) cb(this.status);
  }
}

export const encoder = new TextEncoder();
export const decoder = new TextDecoder();

/**
 * iPadOS reports itself as a Mac, so the user-agent alone is not enough - the
 * touch-point count is what separates an iPad from a desktop Safari.
 */
function isIOS(): boolean {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

/**
 * Naming the browser is the wrong advice on iOS.
 *
 * "Needs Chrome or Edge" invites an iPhone owner to install Chrome and try
 * again, which cannot work: every iOS browser is Safari's engine underneath,
 * and WebKit implements neither of these APIs. The limitation is the platform,
 * not the browser, so the message says that and points at the transport that
 * does work there.
 */
const IOS_REASON =
  'No browser on iOS can do this — Chrome and Edge on iPhone are Safari underneath. ' +
  'Use the Wi-Fi adapter here, or scan from Android or a desktop.';

export function describeTransportSupport(kind: TransportKind): { supported: boolean; reason?: string } {
  switch (kind) {
    case 'wifi':
      return { supported: true };
    case 'sim':
      return { supported: true };
    case 'serial':
      if ('serial' in navigator) return { supported: true };
      return { supported: false, reason: isIOS() ? IOS_REASON : 'Web Serial needs Chrome or Edge on desktop.' };
    case 'ble':
      if ('bluetooth' in navigator) return { supported: true };
      return {
        supported: false,
        reason: isIOS() ? IOS_REASON : 'Web Bluetooth needs Chrome or Edge, on desktop or Android.',
      };
  }
}
